import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';

class ApiError extends Error {
  status: string | number;

  idProofingUrl: string;

  errorMessage: string;

  constructor(
    message: string,
    idProofingUrl: string,
    status: number,
    errorMessage: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.idProofingUrl = idProofingUrl;
    this.status = status;
    this.errorMessage = errorMessage;
    Object.setPrototypeOf(this, ApiError.prototype); // Ensure proper inheritance
  }
}
interface ApiResponse {
  /** Indicates if the API call was successful */
  success: boolean;
  /** Optional message from the server */
  errorMessage: string;
  status: number;
  error: ApiError;
  idProofingUrl: string;
  ok?: boolean;
}

/**
 * Fetches the dashboard details from the external API service.
 *
 * @async
 * @function fetchIDPProviderUrl
 * @returns {Promise<ApiResponse>} The dashboard details retrieved from the API.
 */
const fetchURL = async ({
  key,
  validateLink,
}: {
  key: string;
  validateLink: boolean;
}) => {
  const response = (await ExternalService.getIDPURL(
    key,
    validateLink
  )) as ApiResponse;

  return response;
};

/**
 * Custom hook to retrieve dashboard details using React Query's `useQuery`.
 *
 * @function useGetDashboardDetails
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardResponse, unknown>} A query result object containing the dashboard details, loading state, and potential errors.
 */
const useGetIDPURL = () =>
  useMutation({
    mutationFn: (payload: { key: string; validateLink: boolean }) =>
      fetchURL(payload),
  });

export default useGetIDPURL;
